import { UNLIMITED_QUOTA, isShopMidTrial } from '@/pages/Plan/config';
import { getQuotaPerMonth } from '@/helpers';
import { ProgressPercent } from '@/pages/Dashboard/config';
import { calcPercent, capitalizeFirstLetter } from '@/helpers';
import { useGetUserPlanQuery } from '@/redux/api/api.caller';
import {
  Badge,
  Card,
  Grid,
  InlineStack,
  ProgressBar,
  ProgressBarProps,
  SkeletonBodyText,
  Text,
} from '@shopify/polaris';
import dayjs from 'dayjs';
import { memo, useMemo } from 'react';
import { StyledAppUsage } from './styled';

const AppUsage = () => {
  const { data: userPlanData } = useGetUserPlanQuery({});

  const orderQuotaInfo = useMemo(() => {
    if (userPlanData && userPlanData.data) {
      const { credit, billingSummary } = userPlanData.data;
      const totalQuota = getQuotaPerMonth(userPlanData.data);
      const isUnlimitedQuota = totalQuota === UNLIMITED_QUOTA;
      const progressPercent = isUnlimitedQuota ? 0 : calcPercent(Number(credit), totalQuota);

      const renderProgressBarColor = (): ProgressBarProps['tone'] => {
        if (progressPercent < ProgressPercent.Warning) return 'primary';
        if (progressPercent <= ProgressPercent.Critical) return 'highlight';
        return 'critical';
      };

      return {
        credit,
        totalQuota,
        isUnlimitedQuota,
        progressPercent,
        progressBarColor: renderProgressBarColor(),
        currentPrice: Number(billingSummary.price),
        billingOn:
          billingSummary.nextBillingOn === 'No billing cycle'
            ? 'No billing cycle'
            : dayjs(billingSummary.nextBillingOn).format('MMM D, YYYY'),
        subscription: billingSummary.subscription,
        quotaResetDate: dayjs(billingSummary.quotaResetDay).format('MMM D, YYYY'),
        displayPlan: billingSummary.displayPlan,
        isUsageBillingActive: !!billingSummary.usageBillingActive,
        // credit = trackings used this cycle; totalQuota already includes extra quota.
        usageOverageTrackings: isUnlimitedQuota ? 0 : Math.max(Number(credit) - Number(totalQuota), 0),
        usagePricePerTracking: billingSummary.usagePricePerTracking ?? 0,
        isTrial: isShopMidTrial(userPlanData.data),
        usageBalanceUsed: billingSummary.usageBalanceUsed,
      };
    }
    return null;
  }, [userPlanData]);

  const renderOrderQuotaUI = () => {
    if (!orderQuotaInfo || !userPlanData) {
      return (
        <Card>
          <div className="skeleton">
            <SkeletonBodyText lines={7} />
          </div>
        </Card>
      );
    }
    const {
      isUnlimitedQuota,
      progressBarColor,
      progressPercent,
      credit,
      currentPrice,
      billingOn,
      subscription,
      quotaResetDate,
      totalQuota,
      displayPlan,
      isTrial,
      isUsageBillingActive,
      usageOverageTrackings,
      usagePricePerTracking,
      usageBalanceUsed,
    } = orderQuotaInfo;
    const showOverage = isUsageBillingActive && usageOverageTrackings > 0;
    const usageOverageAmount = usageBalanceUsed ?? usageOverageTrackings * usagePricePerTracking;
    const quotaProgressTone = showOverage && progressBarColor === 'critical' ? 'highlight' : progressBarColor;
    const quotaProgressPercent = showOverage ? Math.min(Number(progressPercent), 100) : Number(progressPercent);
    const usedQuota = showOverage && !isUnlimitedQuota ? Math.min(Number(credit), Number(totalQuota)) : credit;
    const quotaValue = (
      <Text as="p" variant="bodyMd" tone="subdued">
        {isUnlimitedQuota ? capitalizeFirstLetter(UNLIMITED_QUOTA) : `${usedQuota} / ${totalQuota}`}
      </Text>
    );
    const quotaProgress = !isUnlimitedQuota && (
      <div className="usage-progress">
        <ProgressBar progress={quotaProgressPercent} tone={quotaProgressTone} size="small" />
      </div>
    );

    return (
      <Grid
        columns={{
          xl: 3,
          lg: 3,
          md: 1,
          sm: 1,
          xs: 1,
        }}
        gap={{ xs: '1rem' }}
      >
        <Grid.Cell
          columnSpan={{
            xl: 2,
            lg: 2,
            md: 1,
            sm: 1,
            xs: 1,
          }}
        >
          <Card>
            <div className="usage-rows">
              <div className="usage-row">
                <InlineStack gap="200" align="start" blockAlign="center" wrap={false}>
                  <Text as="h6" variant="headingMd">
                    Quota usage
                  </Text>
                  <Badge tone="info">{capitalizeFirstLetter(displayPlan)}</Badge>
                </InlineStack>
              </div>
              {showOverage ? (
                <div className="usage-row usage-row-inline">
                  <div className="usage-used-total">
                    <InlineStack gap="100" blockAlign="baseline" wrap={false}>
                      <Text as="h6" variant="headingSm" tone="subdued">
                        Used / Total:
                      </Text>
                      {quotaValue}
                    </InlineStack>
                  </div>
                  <div className="usage-progress-inline">{quotaProgress}</div>
                </div>
              ) : (
                <>
                  <div className="usage-row">
                    <Text as="h6" variant="headingSm" tone="subdued">
                      Used / Total
                    </Text>
                  </div>
                  <div className="usage-row">{quotaValue}</div>
                  {quotaProgress && <div className="usage-row">{quotaProgress}</div>}
                </>
              )}
              <div className="usage-row">
                <Text as="p" variant="bodyMd" tone="subdued">
                  Resets on {quotaResetDate}
                </Text>
              </div>
              {showOverage && (
                <>
                  <div className="usage-row with-divider">
                    <Text as="h6" variant="headingSm" tone="subdued">
                      Overage charged this cycle
                    </Text>
                    <Text as="p" variant="headingMd" fontWeight="semibold">
                      {`$${usageOverageAmount.toFixed(2)}`}
                    </Text>
                  </div>
                  <div className="usage-row">
                    <Text as="p" variant="bodySm" tone="subdued">
                      {`${usageOverageTrackings.toLocaleString()} shipments × $${usagePricePerTracking.toFixed(
                        2,
                      )}, beyond your ${totalQuota}/month shipment quota`}
                    </Text>
                  </div>
                </>
              )}
            </div>
          </Card>
        </Grid.Cell>
        <Grid.Cell columnSpan={{ xl: 1, lg: 1, md: 1, sm: 1, xs: 1 }}>
          <Card>
            <div className="usage-rows">
              <div className="usage-row">
                <Text as="h6" variant="headingMd">
                  Billing summary
                </Text>
              </div>
              <div className="usage-row">
                <Text as="h6" variant="bodyMd" tone="subdued">
                  Plan
                </Text>
                <InlineStack gap="150" blockAlign="center" wrap={false}>
                  <Text as="h6" variant="bodyMd" fontWeight="semibold">
                    {capitalizeFirstLetter(displayPlan)}
                  </Text>
                  {isTrial && <Badge tone="info">Free trial</Badge>}
                </InlineStack>
              </div>
              <div className="usage-row">
                <Text as="h6" variant="bodyMd" tone="subdued">
                  Billing cycle
                </Text>
                <Text as="h6" variant="bodyMd" fontWeight="semibold">
                  {capitalizeFirstLetter(subscription)}
                </Text>
              </div>
              <div className="usage-row">
                <Text as="h6" variant="bodyMd" tone="subdued">
                  Next billing date
                </Text>
                <Text as="h6" variant="bodyMd" fontWeight="semibold">
                  {billingOn}
                </Text>
              </div>
              <div className="usage-row with-divider">
                <Text as="h6" variant="bodyMd" tone="subdued">
                  Total price
                </Text>
                <Text as="h6" variant="bodyMd" fontWeight="semibold">
                  ${currentPrice}
                </Text>
              </div>
            </div>
          </Card>
        </Grid.Cell>
      </Grid>
    );
  };

  return <StyledAppUsage>{renderOrderQuotaUI()}</StyledAppUsage>;
};

export default memo(AppUsage);
